import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlineInformationCircle } from "react-icons/hi";

import DateTimePicker from "../../components/DateTimePicker";
import {
  getOppositionNoticeOfOpposition,
  saveOppositionNoticeOfOpposition,
  type CounterDeadlineStatus,
} from "../../api/opposition";

export const OPPOSITION_NOTICE_OF_OPPOSITION_FORM_ID =
  "opposition-notice-of-opposition-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDateStr(s: string | null | undefined): Date | null {
  if (!s) return null;
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

function toISODate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function addTwoMonths(d: Date): Date {
  const result = new Date(d);
  result.setMonth(result.getMonth() + 2);
  return result;
}

// ---------------------------------------------------------------------------
// Tooltip: Last Date for Counter calculation
// ---------------------------------------------------------------------------

function CounterDeadlineTooltip() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative inline-flex items-center"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="How is last date for counter calculated?"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        className="flex items-center justify-center text-[#6D6D6D] hover:text-[#222] transition-colors"
      >
        <HiOutlineInformationCircle className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div
          role="tooltip"
          className="absolute top-full left-1/2 -translate-x-1/2 z-50 pt-2"
        >
          <div className="relative w-64 rounded-[8px] border border-[#EEEEEE] bg-white shadow-md px-3 py-2.5 text-[11px] leading-[1.6] text-[#444]">
            <p className="font-semibold text-[#222] mb-1">Auto-calculated</p>
            <p>
              Last Date for Counter = Date of Notice of Opposition{" "}
              <span className="font-medium text-[#222]">+ 2 months</span>
            </p>
            <p className="mt-1 text-[#6D6D6D]">
              e.g. Notice on 15 Jan → Counter deadline 15 Mar
            </p>
            <p className="mt-1 text-[#6D6D6D]">You can still edit this date manually if needed.</p>
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-[#EEEEEE]" />
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Local row type
// ---------------------------------------------------------------------------

type MarkRow = {
  id: string;
  conflictingMark: string | null;
  applicationNo: string | null;
  dateOfNoticeOfOpposition: Date | null;
  lastDateForCounter: Date | null;
  counterDeadlineStatus: CounterDeadlineStatus;
  daysToCounterDeadline: number | null;
};

// ---------------------------------------------------------------------------
// Deadline badge
// ---------------------------------------------------------------------------

function DeadlineBadge({
  status,
  days,
}: {
  status: CounterDeadlineStatus;
  days: number | null;
}) {
  if (status === "NOT_APPLICABLE") return null;

  if (status === "OVERDUE") {
    const absDays = days != null ? Math.abs(days) : null;
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-red-50 text-red-600 border border-red-200 whitespace-nowrap">
        Overdue{absDays != null ? ` (${absDays}d)` : ""}
      </span>
    );
  }

  if (status === "DUE_TODAY") {
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-amber-50 text-amber-600 border border-amber-200 whitespace-nowrap">
        Due today
      </span>
    );
  }

  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium bg-blue-50 text-blue-600 border border-blue-200 whitespace-nowrap">
      {days != null ? `${days}d remaining` : "Pending"}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OppositionNoticeOfOppositionByRegistryView({
  leadId,
  onSuccess,
}: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);
  const [rows, setRows] = useState<MarkRow[]>([]);

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!leadId || loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    setLoading(true);

    getOppositionNoticeOfOpposition(leadId)
      .then((d) => {
        if (cancelled) return;
        setRows(
          d.conflictingMarks.map((cm) => ({
            id: cm.id,
            conflictingMark: cm.conflictingMark,
            applicationNo: cm.applicationNo,
            dateOfNoticeOfOpposition: parseDateStr(cm.dateOfNoticeOfOpposition),
            lastDateForCounter: parseDateStr(cm.lastDateForCounter),
            counterDeadlineStatus: cm.counterDeadlineStatus,
            daysToCounterDeadline: cm.daysToCounterDeadline,
          }))
        );
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err instanceof Error ? err.message : "Failed to load notice data"
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [leadId]);

  // ---------------------------------------------------------------------------
  // Row update
  // ---------------------------------------------------------------------------

  const updateRow = (id: string, updates: Partial<MarkRow>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...updates } : r)));
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    setSaving(true);
    try {
      const saved = await saveOppositionNoticeOfOpposition(leadId, {
        conflictingMarks: rows.map((r) => ({
          conflictingMarkId: r.id,
          dateOfNoticeOfOpposition: toISODate(r.dateOfNoticeOfOpposition),
          lastDateForCounter: toISODate(r.lastDateForCounter),
        })),
      });

      setRows(
        saved.conflictingMarks.map((cm) => ({
          id: cm.id,
          conflictingMark: cm.conflictingMark,
          applicationNo: cm.applicationNo,
          dateOfNoticeOfOpposition: parseDateStr(cm.dateOfNoticeOfOpposition),
          lastDateForCounter: parseDateStr(cm.lastDateForCounter),
          counterDeadlineStatus: cm.counterDeadlineStatus,
          daysToCounterDeadline: cm.daysToCounterDeadline,
        }))
      );

      toast.success("Notice of Opposition saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Early returns
  // ---------------------------------------------------------------------------

  if (!leadId) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">
          Please complete the previous steps to access this step.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="flex flex-col gap-[22px] w-full max-w-[1085px]"
        style={{ fontFamily: "Inter, sans-serif" }}
      >
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  const isMultiApp = rows.length > 1;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <form
      id={OPPOSITION_NOTICE_OF_OPPOSITION_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-6 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {rows.length === 0 ? (
        <p className="text-sm text-[#6D6D6D]">
          No eligible applications found for this step.
        </p>
      ) : (
        rows.map((row, idx) => (
          <div
            key={row.id}
            className={`flex flex-col gap-4 ${
              isMultiApp ? "p-4 rounded-[8px] border border-[#EEEEEE]" : ""
            }`}
          >
            {isMultiApp && (
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <h3 className="text-[13px] font-semibold text-[#222]">
                  {row.conflictingMark ?? `Application ${idx + 1}`}
                  {row.applicationNo ? ` (${row.applicationNo})` : ""}
                </h3>
                <DeadlineBadge
                  status={row.counterDeadlineStatus}
                  days={row.daysToCounterDeadline}
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 21zb — Date of Notice of Opposition */}
              <div className="flex flex-col gap-1">
                <label className={LABEL_CLASS}>
                  Date of Notice of Opposition
                </label>
                <DateTimePicker
                  value={row.dateOfNoticeOfOpposition}
                  onChange={(d) => {
                    const auto = d ? addTwoMonths(d) : null;
                    updateRow(row.id, {
                      dateOfNoticeOfOpposition: d,
                      lastDateForCounter: auto,
                    });
                  }}
                  placeholder="Select Date"
                  buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
                />
              </div>

              {/* 21zc — Last Date for Counter (auto = notice + 2 months, editable) */}
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <label className={LABEL_CLASS}>Last Date for Counter</label>
                  <CounterDeadlineTooltip />
                  {!isMultiApp && (
                    <DeadlineBadge
                      status={row.counterDeadlineStatus}
                      days={row.daysToCounterDeadline}
                    />
                  )}
                </div>
                <DateTimePicker
                  value={row.lastDateForCounter}
                  onChange={(d) => updateRow(row.id, { lastDateForCounter: d })}
                  placeholder="Select Date"
                  buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
                />
              </div>

            </div>
          </div>
        ))
      )}
    </form>
  );
}
